import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useCurrentUserId } from '@framework/auth';
import {
  createWorkspaceEntityRepository,
  type WorkspaceEntityRepository,
  type Workspace,
} from '@workspace/domain/workspace';
import type {
  FolderId,
  GroupId,
  ViewModelId,
  WorkspaceId,
  ViewModelJSON,
  FolderJSON,
  ViewJSON,
  InternalPublicWorkspacesJSON,
} from 'balus-common';
import { ObjectRepository, RecordRepository, RefBuilder, type DataSnapshot } from '@framework/repository';
import { RTDBPath } from 'balus-common';
import { RootFolderTreeRepository } from '@workspace/view-model-folder/infrastructure/FolderTree';
import { ViewModelEntity } from '@view-model/domain/view-model';
import { ViewEntity } from '@view-model/domain/view/ViewEntity';
import { useParams } from 'react-router';
import type { RouteParams } from '@user/pages/RouteParams';
import { Folder } from '@workspace/view-model-folder/domain/Folder';
import type { RootFolderTree } from '@workspace/view-model-folder/domain/FolderTree';
import { KeysRepository } from '@framework/repository/Collection';
import { usePrevious } from '@view-model/models/common/hooks/usePrevious';
import { GroupIpRestrictionCheckRequest } from '@group/AccessControl';
import { useCurrentSessionId } from '@framework/auth/authStatus';
import { uniq } from 'lodash-es';
import { useAtomValue, useSetAtom } from 'jotai';
import {
  controlAtom,
  setLastOpenedAtom,
  setScrolledFolderAtom,
  setScrolledViewModelAtom,
  setScrolledWorkspaceAtom,
} from './SidebarTreeStore/sidebarTreeControlState';
import {
  openFoldersAtom,
  openFolderIdsAtom,
  openWorkspaceAtom,
  openWorkspaceIdsAtom,
  openViewModelIdsAtom,
  resetOpenStateAtom,
} from './SidebarTreeStore/sidebarTreeOpenState';
import {
  folderTreesAtom,
  firstLoadAtom,
  resetViewsAtom,
  setInternalPublicWorkspacesAtom,
  addWorkspaceAtom,
  addRootFolderTreeAtom,
  setFolderAtom,
  removeFolderAtom,
  setViewModelAtom,
  removeViewModelAtom,
  removeBulkAtom,
  setViewAtom,
  removeViewAtom,
} from './SidebarTreeStore/sidebarTreeRecordsState';

const fetchFoldersAndViewModels = async (
  targetFolderIds: FolderId[],
  targetViewModelIds: ViewModelId[]
): Promise<[Folder[], ViewModelEntity[]]> => {
  return await Promise.all([
    Promise.all(
      targetFolderIds.map((folderId) => new ObjectRepository(Folder, RTDBPath.Workspace.folderPath(folderId)).get())
    ),
    Promise.all(
      targetViewModelIds.map((viewModelId) =>
        new ObjectRepository(ViewModelEntity, RTDBPath.ViewModel.viewModelPath(viewModelId)).get()
      )
    ),
  ]).then(([fetchedFolders, fetchedViewModels]) => {
    return [
      fetchedFolders.filter((folder): folder is Folder => !!folder),
      fetchedViewModels.filter((viewModel): viewModel is ViewModelEntity => !!viewModel),
    ];
  });
};

type RepoRecords = {
  workspaceEntityRepos: Record<WorkspaceId, WorkspaceEntityRepository>;
  folderTreeRepos: Record<WorkspaceId, RootFolderTreeRepository>;
  folderRepos: Record<FolderId, ObjectRepository<FolderJSON, Folder>>;
  viewModelRepos: Record<ViewModelId, ObjectRepository<ViewModelJSON, ViewModelEntity>>;
  viewRecordRepos: Record<ViewModelId, RecordRepository<ViewJSON, ViewEntity>>;
};

// 各リポジトリのマップは複数のuseEffectがオブジェクト参照を保持しているため、
// 新しいオブジェクトへの差し替えではなく中身の削除でクリアする
const clearAllRepoListeners = (repos: RepoRecords): void => {
  Object.keys(repos.workspaceEntityRepos).forEach((id) => {
    repos.workspaceEntityRepos[id].removeListener();
    delete repos.workspaceEntityRepos[id];
  });
  Object.keys(repos.folderTreeRepos).forEach((id) => {
    repos.folderTreeRepos[id].off();
    delete repos.folderTreeRepos[id];
  });
  Object.keys(repos.folderRepos).forEach((id) => {
    repos.folderRepos[id].removeListener();
    delete repos.folderRepos[id];
  });
  Object.keys(repos.viewModelRepos).forEach((id) => {
    repos.viewModelRepos[id].removeListener();
    delete repos.viewModelRepos[id];
  });
  Object.keys(repos.viewRecordRepos).forEach((id) => {
    repos.viewRecordRepos[id].removeListener();
    delete repos.viewRecordRepos[id];
  });
};

export const useSidebarTree = (
  groupId: string
): {
  firstLoaded: boolean;
} => {
  const currentUserId = useCurrentUserId();
  const currentSessionId = useCurrentSessionId();
  const [firstLoaded, setFirstLoaded] = useState(false);
  const {
    workspaceId: workspaceIdParam,
    viewModelId: viewModelIdParam,
    folderId: folderIdParam,
  } = useParams<RouteParams>();

  const setOpenWorkspace = useSetAtom(openWorkspaceAtom);
  const setOpenFolders = useSetAtom(openFoldersAtom);
  const resetOpenState = useSetAtom(resetOpenStateAtom);
  const setLastOpened = useSetAtom(setLastOpenedAtom);
  const setScrolledWorkspace = useSetAtom(setScrolledWorkspaceAtom);
  const setScrolledFolder = useSetAtom(setScrolledFolderAtom);
  const setScrolledViewModel = useSetAtom(setScrolledViewModelAtom);

  const folderTrees = useAtomValue(folderTreesAtom);

  const setFirstLoad = useSetAtom(firstLoadAtom);
  const resetViews = useSetAtom(resetViewsAtom);
  const setInternalPublicWorkspaces = useSetAtom(setInternalPublicWorkspacesAtom);
  const addWorkspace = useSetAtom(addWorkspaceAtom);
  const addRootFolderTree = useSetAtom(addRootFolderTreeAtom);
  const setFolder = useSetAtom(setFolderAtom);
  const removeFolder = useSetAtom(removeFolderAtom);
  const setViewModel = useSetAtom(setViewModelAtom);
  const removeViewModel = useSetAtom(removeViewModelAtom);
  const removeBulk = useSetAtom(removeBulkAtom);
  const setView = useSetAtom(setViewAtom);
  const removeView = useSetAtom(removeViewAtom);

  const previousGroupIdRef = useRef(groupId);
  const workspaceIdsRepoRef = useRef<KeysRepository<WorkspaceId> | null>(null);
  const workspaceEntityReposRef = useRef<Record<WorkspaceId, WorkspaceEntityRepository>>({});
  const folderTreeReposRef = useRef<Record<WorkspaceId, RootFolderTreeRepository>>({});
  const folderReposRef = useRef<Record<FolderId, ObjectRepository<FolderJSON, Folder>>>({});
  const viewModelReposRef = useRef<Record<ViewModelId, ObjectRepository<ViewModelJSON, ViewModelEntity>>>({});
  const viewRecordReposRef = useRef<Record<ViewModelId, RecordRepository<ViewJSON, ViewEntity>>>({});

  const control = useAtomValue(controlAtom);

  // グループ内の別のワークスペースページビューモデルページに遷移時やURLに直接アクセスした時などに、
  // サイドバー上でアクセス先に対応したワークスペースやフォルダを展開するためのuseEffect
  useEffect(() => {
    if (!currentUserId) return;

    if (folderIdParam && workspaceIdParam) {
      const rootFolderTree = folderTrees[workspaceIdParam];
      if (!rootFolderTree) return;

      const ancestorFolderIds = rootFolderTree
        .getAncestorFolderIds(folderIdParam)
        .filter((id) => id !== Folder.ROOT_ID);

      setOpenFolders({ folderIds: [...ancestorFolderIds, folderIdParam], workspaceId: workspaceIdParam });

      return;
    }

    // lastOpenedWorkspaceIdとworkspaceIdParamを比較することで、ワークスペースページを表示した際に
    // サイドバーの該当ワークスペースが展開されっぱなしになるのを防ぐ
    if (workspaceIdParam && workspaceIdParam !== control.lastOpenedWorkspaceId) {
      setOpenWorkspace(workspaceIdParam);
      setLastOpened({ lastOpenedWorkspaceId: workspaceIdParam });

      return;
    }

    // lastOpenedViewModelIdとviewModelIdParamを比較することで、ワークスペースページを表示した際に
    // サイドバーの該当ワークスペースが展開されっぱなしになるのを防ぐ
    if (viewModelIdParam && viewModelIdParam !== control.lastOpenedViewModelId) {
      const result = Object.entries(folderTrees).find(([, folderTree]) =>
        folderTree.hasDescendantViewModel(viewModelIdParam)
      );
      if (!result) return;

      const [workspaceId, rootFolderTree] = result;
      const parentFolderId = rootFolderTree.getParentFolderId(viewModelIdParam);
      if (!parentFolderId) return;

      const ancestorFolderIds = rootFolderTree
        .getAncestorFolderIds(parentFolderId)
        .filter((id) => id !== Folder.ROOT_ID);

      setOpenFolders({
        workspaceId,
        folderIds: [...ancestorFolderIds, parentFolderId],
      });
    }
  }, [
    folderIdParam,
    workspaceIdParam,
    viewModelIdParam,
    folderTrees,
    currentUserId,
    control.lastOpenedWorkspaceId,
    control.lastOpenedViewModelId,
    setOpenFolders,
    setOpenWorkspace,
    setLastOpened,
  ]);

  // ページ遷移時にスクロールするべきワークスペースやビューモデルを指定するuseLayoutEffect
  // useEffectだと対象のビューモデルへスクロール後に他のビューモデルが描画されてしまい、
  // 結果的に対象のビューモデルが画面外に押し出されてしまう
  // そのため、useEffectの代わりにuseLayoutEffectを使用している
  useLayoutEffect(() => {
    if (folderIdParam) {
      setScrolledFolder(folderIdParam);
      return;
    }
    if (workspaceIdParam) {
      setScrolledWorkspace(workspaceIdParam);
      return;
    }
    if (viewModelIdParam) {
      setScrolledViewModel(viewModelIdParam);
    }
  }, [
    workspaceIdParam,
    viewModelIdParam,
    folderIdParam,
    setScrolledFolder,
    setScrolledWorkspace,
    setScrolledViewModel,
  ]);

  // groupIdが変わったときに旧グループ向けのリスナーと取得済みデータを破棄して、初回取得からやり直すuseEffect
  // サイドバーはページ遷移をまたいで常駐するため、これがないとグループを跨ぐ遷移後も
  // 旧グループのワークスペース一覧を購読・表示し続けてしまう
  useEffect(() => {
    if (previousGroupIdRef.current === groupId) return;

    // useDetectGroupIdは空文字から実IDへ非同期に解決するため、初回の解決はグループ切替として扱わない
    // （ここでリセットするとroute paramに応じて展開済みの開閉状態が消え、
    // lastOpenedとの比較ガードにより二度と展開されなくなる）
    const isInitialDetection = !previousGroupIdRef.current;
    previousGroupIdRef.current = groupId;
    if (isInitialDetection) return;

    workspaceIdsRepoRef.current?.removeListener();
    workspaceIdsRepoRef.current = null;

    clearAllRepoListeners({
      workspaceEntityRepos: workspaceEntityReposRef.current,
      folderTreeRepos: folderTreeReposRef.current,
      folderRepos: folderReposRef.current,
      viewModelRepos: viewModelReposRef.current,
      viewRecordRepos: viewRecordReposRef.current,
    });

    // 開閉状態をリセットしないと、旧グループで開いていたビューモデルのIDが残り、
    // 再ロード後にビュー監視のuseEffectが旧グループのビューを再購読してしまう
    resetOpenState();
    // lastOpenedを残すと、遷移先ワークスペースが「既に開いた」と判定されて再展開されない
    setLastOpened({ lastOpenedWorkspaceId: '' });
    resetViews();
    setFirstLoad({ workspaces: {}, internalPublicWorkspaces: {}, folderTrees: {}, folders: {}, viewModels: {} });
    setFirstLoaded(false);
  }, [groupId, setFirstLoad, resetOpenState, setLastOpened, resetViews]);

  // 初回取得用のuseEffect
  useEffect(() => {
    if (!currentUserId) return;
    if (!groupId) return;
    if (firstLoaded) return;

    if (!workspaceIdsRepoRef.current) {
      workspaceIdsRepoRef.current = new KeysRepository<WorkspaceId>(
        RTDBPath.Workspace.assignedGroupWorkspaceIndexPath(currentUserId, groupId)
      );
    }
    const workspaceIdsRepo = workspaceIdsRepoRef.current;

    // 初回のみワークスペースとルートフォルダツリーをまとめて取得する
    // また、アクセスしたページに応じて、関連するフォルダやビューモデルの取得も行う
    const fetchFirstLoad = async (): Promise<{
      workspaces: Record<WorkspaceId, Workspace>;
      internalPublicWorkspaces: Record<GroupId, InternalPublicWorkspacesJSON>;
      folderTrees: Record<WorkspaceId, RootFolderTree>;
      folders: Record<FolderId, Folder>;
      viewModels: Record<ViewModelId, ViewModelEntity>;
    }> => {
      await GroupIpRestrictionCheckRequest.waitAllow(groupId, currentSessionId);

      const [workspaceIds, internalPublicWorkspaces] = await Promise.all([
        workspaceIdsRepo.get(),
        // 取得に失敗しても初回ロード全体を巻き込まず、従来どおり lock 表示のまま進める
        RefBuilder.ref(RTDBPath.Group.internalPublicWorkspacesPath(groupId))
          .get()
          .then((snapshot): InternalPublicWorkspacesJSON => snapshot.val() ?? {})
          .catch((): InternalPublicWorkspacesJSON => ({})),
      ]);

      const workspaces: Workspace[] = (
        await Promise.all(
          workspaceIds.map(async (id) => {
            const repo = createWorkspaceEntityRepository(id);
            return repo.get();
          })
        )
      ).filter((w): w is Workspace => !!w);

      // 共有マップ（folderTreeReposRef等）への書き込みはここでは行わない。
      // ロード完了前のページ遷移で破棄された取得処理が後から共有マップを書き換えると、
      // リスナー登録済みのインスタンスがマップから外れて解除できなくなるため
      const folderTrees: Record<WorkspaceId, RootFolderTree> = Object.fromEntries(
        await Promise.all(
          workspaces.map(async (w) => {
            const rootFolderTree = await new RootFolderTreeRepository(w.id).load();
            return [w.id, rootFolderTree];
          })
        )
      );

      // 関連するフォルダやビューモデルの取得も行う
      let targetFolderIds: FolderId[] = [];
      let targetViewModelIds: ViewModelId[] = [];
      if (folderIdParam) {
        const rootFolderTree = Object.values(folderTrees).find((folderTree) => folderTree.has(folderIdParam));
        if (rootFolderTree) {
          const ancestorFolderIds = rootFolderTree.getAncestorFolderIds(folderIdParam);

          targetFolderIds = uniq(
            [folderIdParam, ...ancestorFolderIds].flatMap((folderId) => rootFolderTree.getFolderIdsOf(folderId))
          );
          targetViewModelIds = uniq(
            [folderIdParam, ...ancestorFolderIds].flatMap((folderId) => rootFolderTree.getViewModelIdsOf(folderId))
          );
        }
      } else if (workspaceIdParam) {
        const rootFolderTree = folderTrees[workspaceIdParam];
        if (rootFolderTree) {
          const folderIds = rootFolderTree.getFolderIdsOfRoot();
          const viewModelIds = rootFolderTree.getViewModelIdsOfRoot();

          targetFolderIds = uniq(folderIds);
          targetViewModelIds = uniq(viewModelIds);
        }
      } else if (viewModelIdParam) {
        const rootFolderTree = Object.values(folderTrees).find((folderTree) => folderTree.has(viewModelIdParam));
        if (rootFolderTree) {
          const parentFolderId = rootFolderTree.getParentFolderId(viewModelIdParam);
          if (parentFolderId) {
            const ancestorFolderIds = rootFolderTree.getAncestorFolderIds(parentFolderId);

            targetFolderIds = uniq(
              [parentFolderId, ...ancestorFolderIds].flatMap((folderId) => rootFolderTree.getFolderIdsOf(folderId))
            );
            targetViewModelIds = uniq(
              [parentFolderId, ...ancestorFolderIds].flatMap((folderId) => rootFolderTree.getViewModelIdsOf(folderId))
            );
          }
        }
      }

      const [fetchedFolders, fetchedViewModels] = await fetchFoldersAndViewModels(targetFolderIds, targetViewModelIds);

      return {
        workspaces: Object.fromEntries(workspaces.map((ws) => [ws.id, ws])),
        internalPublicWorkspaces: { [groupId]: internalPublicWorkspaces },
        folderTrees,
        folders: Object.fromEntries(fetchedFolders.map((folder) => [folder.id, folder])),
        viewModels: Object.fromEntries(fetchedViewModels.map((viewModel) => [viewModel.id, viewModel])),
      };
    };

    let cancelled = false;
    fetchFirstLoad().then((values) => {
      if (cancelled) return;
      setFirstLoad(values);
      setFirstLoaded(true);
    });

    // ロード完了前にroute paramの変化などでeffectが再実行されたときは、
    // 先に発行した取得の結果が最新の取得結果を上書きしないように破棄する
    return () => {
      cancelled = true;
    };
  }, [
    firstLoaded,
    groupId,
    currentUserId,
    currentSessionId,
    setFirstLoad,
    folderIdParam,
    viewModelIdParam,
    workspaceIdParam,
  ]);

  // グループ内公開設定の監視。初回値は fetchFirstLoad が firstLoadAtom 経由で投入するため、
  // firstLoaded 後に監視を開始する。route param に依存する初回取得用の useEffect に含めると
  // ページ遷移のたびに off→on の再登録と value の即時発火が起きるため、独立した useEffect にしている。
  useEffect(() => {
    if (!firstLoaded) return;
    if (!groupId) return;

    const ref = RefBuilder.ref(RTDBPath.Group.internalPublicWorkspacesPath(groupId));
    const callback = (snapshot: DataSnapshot) => {
      setInternalPublicWorkspaces({ groupId, internalPublicWorkspaces: snapshot.val() ?? {} });
    };
    ref.on('value', callback);

    return () => {
      ref.off('value', callback);
    };
  }, [firstLoaded, groupId, setInternalPublicWorkspaces]);
  // 所属ワークスペースの増減を監視するuseEffect
  // route paramに依存させるとページ遷移のたびにaddListenerが呼ばれ、各Repositoryは旧コールバックを
  // offせずに上書きする実装のためRTDBのリスナーが累積してしまう。
  // そのため初回取得用のuseEffectとは分離し、初回取得完了後に一度だけリスナーを登録する
  useEffect(() => {
    if (!firstLoaded) return;

    const workspaceIdsRepo = workspaceIdsRepoRef.current;
    if (!workspaceIdsRepo) return;

    const workspaceEntityRepos = workspaceEntityReposRef.current;
    const folderTreeRepos = folderTreeReposRef.current;

    workspaceIdsRepo.addListener(
      // 所属ワークスペースが増えた時
      (workspaceId) => {
        // ワークスペースの監視
        if (!workspaceEntityRepos[workspaceId]) {
          workspaceEntityRepos[workspaceId] = createWorkspaceEntityRepository(workspaceId);
        }
        workspaceEntityRepos[workspaceId].addListener((workspace) => {
          if (!workspace) return;
          addWorkspace({
            workspace,
          });
        });

        // フォルダツリーの監視
        if (!folderTreeRepos[workspaceId]) {
          folderTreeRepos[workspaceId] = new RootFolderTreeRepository(workspaceId);
        }
        folderTreeRepos[workspaceId].on((rootFolderTree) => {
          addRootFolderTree({
            workspaceId,
            rootFolderTree,
          });
        });
      },
      // 所属ワークスペースが減った時
      (workspaceId) => {
        // ワークスペースの監視解除
        workspaceEntityRepos[workspaceId]?.removeListener();
        delete workspaceEntityRepos[workspaceId];

        // フォルダツリーの監視解除
        folderTreeRepos[workspaceId]?.off();
        delete folderTreeRepos[workspaceId];

        // 削除されたワークスペースに紐づく各種データをstoreから削除
        removeBulk({
          workspaceId,
        });
      }
    );

    return () => {
      workspaceIdsRepo.removeListener();
      Object.values(workspaceEntityRepos).forEach((repo) => repo.removeListener());
      Object.values(folderTreeRepos).forEach((repo) => repo.off());
    };
  }, [firstLoaded, addWorkspace, addRootFolderTree, removeBulk]);

  const previousFolderTrees = usePrevious(folderTrees);

  // フォルダの新規作成や削除を監視するuseEffect
  useEffect(() => {
    if (!previousFolderTrees) return;
    const treeToIds = (folderTrees: Record<WorkspaceId, RootFolderTree>): FolderId[] => {
      const ids: FolderId[] = [];
      Object.values(folderTrees).forEach((folderTree) => {
        ids.push(...folderTree.getAllFolderIds());
      });
      return ids;
    };

    const previousIds = treeToIds(previousFolderTrees || {});
    const currentIds = treeToIds(folderTrees);

    // フォルダの全件監視を避けるために、全フォルダツリーのロードが完了するまではフォルダの新規作成や削除を監視しない
    if (previousIds.length <= 0) return;

    const addedIds = currentIds.filter((id) => !previousIds.includes(id));
    const removedIds = previousIds.filter((id) => !currentIds.includes(id));

    addedIds.forEach((id) => {
      const repo = new ObjectRepository(Folder, RTDBPath.Workspace.folderPath(id));
      folderReposRef.current[id] = repo;
      repo.addListener((folder) => {
        if (folder) {
          setFolder({
            folder,
          });
        } else {
          removeFolder({
            folderId: id,
          });
        }
      });
    });

    removedIds.forEach((id) => {
      folderReposRef.current[id]?.removeListener();
      delete folderReposRef.current[id];
      removeFolder({
        folderId: id,
      });
    });
  }, [folderTrees, previousFolderTrees, setFolder, removeFolder]);

  // ビューモデルの新規作成や削除を監視する
  useEffect(() => {
    // RootFolderTreeのレコードから、全てのビューモデルIDの配列を生成
    const treeToIds = (folderTrees: Record<WorkspaceId, RootFolderTree>): ViewModelId[] => {
      const ids: ViewModelId[] = [];
      Object.values(folderTrees).forEach((folderTree) => {
        ids.push(...folderTree.getAllViewModelIds());
      });
      return ids;
    };
    const previousIds = treeToIds(previousFolderTrees || {});
    const currentIds = treeToIds(folderTrees);

    // ビューモデルの全件監視を避けるために、全フォルダツリーのロードが完了するまではビューモデルの新規作成や削除を監視しない
    if (previousIds.length <= 0) return;

    const addedIds = currentIds.filter((id) => !previousIds.includes(id));
    const removedIds = previousIds.filter((id) => !currentIds.includes(id));

    addedIds.forEach((id) => {
      // すでに監視対象になっている場合は何もしない
      if (viewModelReposRef.current[id]) return;

      const repo = new ObjectRepository(ViewModelEntity, RTDBPath.ViewModel.viewModelPath(id));
      viewModelReposRef.current[id] = repo;
      repo.addListener((viewModel) => {
        if (viewModel) {
          setViewModel({
            viewModel,
          });
        } else {
          removeViewModel({
            viewModelId: id,
          });
        }
      });
    });

    removedIds.forEach((id) => {
      viewModelReposRef.current[id]?.removeListener();
      delete viewModelReposRef.current[id];

      // ViewModelが削除された時は、関連するViewRecordRepositoryも削除
      viewRecordReposRef.current[id]?.removeListener();
      delete viewRecordReposRef.current[id];

      removeViewModel({
        viewModelId: id,
      });
    });
  }, [folderTrees, previousFolderTrees, setViewModel, removeViewModel]);

  const openWorkspaceIds = useAtomValue(openWorkspaceIdsAtom);
  const openFolderIds = useAtomValue(openFolderIdsAtom);
  const openViewModelIds = useAtomValue(openViewModelIdsAtom);

  // ワークスペースやフォルダが開かれた時にその子供を監視するuseEffect
  useEffect(() => {
    if (!firstLoaded || Object.keys(folderTrees).length <= 0) return;

    const currentFolderRepos = folderReposRef.current;
    const currentViewModelRepos = viewModelReposRef.current;

    const currentOpenWorkspaceIds = Object.entries(openWorkspaceIds)
      .filter(([, isOpen]) => isOpen)
      .map(([id]) => id);
    const currentOpenFolderIds = Object.entries(openFolderIds)
      .filter(([, isOpen]) => isOpen)
      .map(([id]) => id);

    if (currentOpenFolderIds.length <= 0 && currentOpenWorkspaceIds.length <= 0) return;
    // Repoがない＝まだ監視されていないフォルダやビューモデルのIDを取得
    const targetChildrenFolderIdsOfWorkspaces = currentOpenWorkspaceIds
      .flatMap((id) => folderTrees[id]?.getFolderIdsOfRoot() || [])
      .filter((id) => !currentFolderRepos[id]);
    const targetChildrenViewModelIdsOfWorkspaces = currentOpenWorkspaceIds
      .flatMap((id) => folderTrees[id]?.getViewModelIdsOfRoot() || [])
      .filter((id) => !currentViewModelRepos[id]);

    const targetChildrenFolderIdsOfFolders = currentOpenFolderIds
      .flatMap(
        (id) =>
          Object.values(folderTrees)
            .find((folderTree) => folderTree.has(id))
            ?.getFolderIdsOf(id) || []
      )
      .filter((id) => !currentFolderRepos[id]);
    const targetChildrenViewModelIdsOfFolders = currentOpenFolderIds
      .flatMap(
        (id) =>
          Object.values(folderTrees)
            .find((folderTree) => folderTree.has(id))
            ?.getViewModelIdsOf(id) || []
      )
      .filter((id) => !currentViewModelRepos[id]);

    uniq([...targetChildrenFolderIdsOfWorkspaces, ...targetChildrenFolderIdsOfFolders]).forEach((id) => {
      const repo = new ObjectRepository(Folder, RTDBPath.Workspace.folderPath(id));
      folderReposRef.current[id] = repo;
      repo.addListener((folder) => {
        if (folder) {
          setFolder({
            folder,
          });
        } else {
          removeFolder({
            folderId: id,
          });
        }
      });
    });
    uniq([...targetChildrenViewModelIdsOfWorkspaces, ...targetChildrenViewModelIdsOfFolders]).forEach((id) => {
      // すでに監視対象になっている場合は何もしない
      if (viewModelReposRef.current[id]) return;

      const repo = new ObjectRepository(ViewModelEntity, RTDBPath.ViewModel.viewModelPath(id));
      viewModelReposRef.current[id] = repo;
      repo.addListener((viewModel) => {
        if (viewModel) {
          setViewModel({
            viewModel,
          });
        } else {
          removeViewModel({
            viewModelId: id,
          });
        }
      });
    });
  }, [
    openWorkspaceIds,
    openFolderIds,
    folderTrees,
    firstLoaded,
    setFolder,
    removeFolder,
    setViewModel,
    removeViewModel,
  ]);

  // ビューモデルが開かれた時にその子供（ビュー）を監視するuseEffect
  useEffect(() => {
    if (!firstLoaded) return;

    const currentOpenViewModelIds = Object.entries(openViewModelIds)
      .filter(([, isOpen]) => isOpen)
      .map(([id]) => id);

    // まだ監視されていないViewModelIdを対象にする
    const targetViewModelIds = currentOpenViewModelIds.filter((id) => !viewRecordReposRef.current[id]);

    targetViewModelIds.forEach((viewModelId) => {
      const viewRecordRepo = new RecordRepository(ViewEntity, RTDBPath.View.viewsPath(viewModelId));
      viewRecordReposRef.current[viewModelId] = viewRecordRepo;

      // RecordRepositoryのaddListenerでビューの追加・更新・削除を監視
      viewRecordRepo.addListener(
        (view: ViewEntity) => {
          setView({ view, viewModelId });
        },
        (view: ViewEntity) => {
          setView({ view, viewModelId });
        },
        (view: ViewEntity) => {
          removeView({ viewId: view.id, viewModelId });
        }
      );
    });
  }, [openViewModelIds, firstLoaded, setView, removeView]);

  useEffect(() => {
    const workspaceEntityRepos = workspaceEntityReposRef.current;
    const folderTreeRepos = folderTreeReposRef.current;
    const folderRepos = folderReposRef.current;
    const viewModelRepos = viewModelReposRef.current;
    const viewRecordRepos = viewRecordReposRef.current;
    return () => {
      clearAllRepoListeners({
        workspaceEntityRepos,
        folderTreeRepos,
        folderRepos,
        viewModelRepos,
        viewRecordRepos,
      });
    };
  }, []);

  return { firstLoaded };
};
